import React, { useState, useEffect } from 'react';
import '../../styles/EmployeeManagement.css';
import SearchableSelect from '../shared/SearchableSelect';
import discoCodes from '../../data/disco-codes.json';
import api from '../../services/api';

const formatAmount = (amount) => {
    if (!amount) return '0,00';
    return new Intl.NumberFormat('da-DK', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
        useGrouping: true
    }).format(amount);
};

const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('da-DK', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
    }).replace(/\//g, '.');
};

// Hjælpefunktion til at parse dansk talformat til float
const parseAmount = (value) => {
    if (typeof value === 'number') return value;
    if (!value) return '';
    // Fjern punktummer (tusindtalsseparator) og erstat komma med punktum
    const cleaned = value.replace(/\./g, '').replace(',', '.');
    const num = parseFloat(cleaned);
    return isNaN(num) ? '' : num;
};

// Tilføj CPR-nummer validering
const validateCpr = (cpr) => {
    if (!cpr) return false;
    // Fjern alle ikke-cifre
    const cleanCpr = cpr.replace(/\D/g, '');
    // Tjek om det er 10 cifre
    if (cleanCpr.length !== 10) return false;
    // Tjek om det følger formatet DDMMYY-XXXX
    const cprRegex = /^(\d{2})(\d{2})(\d{2})-(\d{4})$/;
    return cprRegex.test(cpr);
};

const EmployeeManagement = ({ isEditingAllowed, selectedMonth, selectedYear }) => {
    const [showModal, setShowModal] = useState(false);
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [modalError, setModalError] = useState(null);
    const [templates, setTemplates] = useState([]);
    const [payrollSystem, setPayrollSystem] = useState(null);
    const [employeeFields, setEmployeeFields] = useState([]);
    const [kundeData, setKundeData] = useState(null);
    const [newEmployee, setNewEmployee] = useState({});
    const [activeStep, setActiveStep] = useState(1);
    const [dropdownItems, setDropdownItems] = useState({}); // { [dropdown_list_id]: [items] }
    const [allEmployees, setAllEmployees] = useState([]);
    const [afregningsformer, setAfregningsformer] = useState([]);
    const [inputRawValues, setInputRawValues] = useState({});
    const [isSaving, setIsSaving] = useState(false);

    // Hent medarbejdere, feltindstillinger og kundeoplysninger ved komponent mount og når måned/år ændres
    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                setError(null);
                const [employeesResponse, fieldsResponse, kundeResponse, listsResponse, afregningsformerResponse] = await Promise.all([
                    api.getEmployees(),
                    api.get('/employee-fields'),
                    api.get('/info'),
                    api.get('/dropdown-lists'),
                    api.get('/payment-forms'),
                ]);
                
                // Filtrer medarbejdere baseret på måned/år
                const filteredEmployees = employeesResponse.employees.filter(employee => 
                    employee.add_month === selectedMonth && 
                    employee.add_year === selectedYear
                );
                
                setEmployees(filteredEmployees);
                setAllEmployees(employeesResponse.employees);
                setPayrollSystem(employeesResponse.payrollSystem);
                setEmployeeFields(fieldsResponse);
                setKundeData(kundeResponse);
                setAfregningsformer(afregningsformerResponse);
                // Hent alle dropdown-items for alle lister
                const itemsObj = {};
                await Promise.all(listsResponse.map(async (list) => {
                    const items = await api.get(`/dropdown-lists/${list.id}/items`);
                    itemsObj[list.id] = items;
                }));
                setDropdownItems(itemsObj);
            } catch (error) {
                setError('Der opstod en fejl ved hentning af data');
                console.error('Fejl ved hentning af data:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [selectedMonth, selectedYear]);

    // Synkroniser input-states med newEmployee, når modal åbnes eller medarbejder skiftes
    useEffect(() => {
        if (showModal) {
            // Fjernet ubrugte state-opdateringer
        }
        // eslint-disable-next-line
    }, [showModal, newEmployee.salary, newEmployee.weekly_hours, newEmployee.monthly_hours, newEmployee.ft_percentage, newEmployee.care_days]);

    const handleAddNew = () => {
        setActiveStep(1);
        setModalError(null);
        // Dynamisk initialisering af newEmployee med default_value fra employeeFields
        const initialEmployee = employeeFields.reduce((acc, field) => {
            let value = field.default_value;
            // Særlig håndtering for use_nemkonto: brug kundeData hvis tilgængelig
            if (field.field_name === 'use_nemkonto' && kundeData && typeof kundeData.use_nemkonto !== 'undefined') {
                value = !!kundeData.use_nemkonto;
            } else if (field.field_type === 'boolean') {
                value = value === 'true' || value === true;
            } else if (field.field_type === 'number' && value !== undefined && value !== null && value !== '') {
                value = Number(value);
            } else if (value === undefined || value === null) {
                value = '';
            }
            acc[field.field_name] = value;
            return acc;
        }, {});

        // Sæt standard afregningsform
        if (afregningsformer && afregningsformer.length > 0) {
            if (afregningsformer.length === 1) {
                // Hvis der kun er én afregningsform, vælg den
                initialEmployee.salary_group = afregningsformer[0].id;
            } else {
                // Find "Månedsvis, bagud" hvis den findes
                const bagudForm = afregningsformer.find(form => 
                    form.name.toLowerCase().includes('månedsvis') && 
                    form.name.toLowerCase().includes('bagud')
                );
                if (bagudForm) {
                    initialEmployee.salary_group = bagudForm.id;
                }
            }
        }

        setNewEmployee({ ...initialEmployee, copyFromType: '', status: 'pending' });
        setShowModal(true);
    };

    // Hjælpefunktion til at konvertere UTC-dato til lokal dato (YYYY-MM-DD)
    const toLocalDateString = (utcString) => {
        if (!utcString) return '';
        const date = new Date(utcString);
        const offset = date.getTimezoneOffset();
        date.setMinutes(date.getMinutes() - offset);
        return date.toISOString().substring(0, 10);
    };

    // Opdater validateStep funktionen
    const validateStep = (step) => {
        const errors = [];
        // Find alle sektioner for det aktuelle step
        const group = sectionGroups[step - 1];
        if (!group) return errors;
        // Saml alle felter fra de relevante sektioner
        const fieldsInStep = employeeFields.filter(f => group.sections.includes(f.section));
        fieldsInStep.forEach(field => {
            if (field.is_active && field.is_required && !newEmployee[field.field_name]) {
                errors.push(`${field.display_name} er påkrævet`);
            }
            // Tilføj CPR-nummer validering
            if (field.field_name === 'cpr' && newEmployee[field.field_name]) {
                if (!validateCpr(newEmployee[field.field_name])) {
                    errors.push('CPR-nummer skal være i formatet DDMMYY-XXXX');
                }
            }
        });
        return errors;
    };

    // Opdater handleNextStep
    const handleNextStep = () => {
        const errors = validateStep(activeStep);
        if (errors.length > 0) {
            setModalError(errors.join('\n'));
            return;
        }
        setModalError(null);
        if (activeStep < 4) {
            setActiveStep(activeStep + 1);
            const modalContent = document.querySelector('.employee-modal-content');
            if (modalContent) {
                modalContent.scrollTop = 0;
            }
        }
    };

    // Opdater handleSave
    const handleSave = async () => {
        // Valider det aktuelle step før gem
        const errors = validateStep(activeStep);
        if (errors.length > 0) {
            setModalError(errors.join('\n'));
            return;
        }
        try {
            setIsSaving(true);
            setModalError(null);
            // Tilføj add_month og add_year til den nye medarbejder
            const employeeData = {
                ...newEmployee,
                add_month: selectedMonth,
                add_year: selectedYear,
                // Sikr at boolean værdier er korrekte
                use_nemkonto: Boolean(newEmployee.use_nemkonto),
                free_phone: Boolean(newEmployee.free_phone),
                free_car: Boolean(newEmployee.free_car),
                health_insurance: Boolean(newEmployee.health_insurance),
                staff_association: Boolean(newEmployee.staff_association),
                lunch_arrangement: Boolean(newEmployee.lunch_arrangement),
                free_choice: newEmployee.free_choice ? 1 : 0,
                // Konverter numeriske værdier
                salary: newEmployee.salary ? parseFloat(newEmployee.salary) : null,
                hourly_rate: newEmployee.hourly_rate ? parseFloat(newEmployee.hourly_rate) : null,
                weekly_hours:newEmployee.weekly_hours ? parseFloat(newEmployee.weekly_hours) : null, 
                monthly_hours: newEmployee.monthly_hours ? parseFloat(newEmployee.monthly_hours) : null, 
                extra_tax_percentage: newEmployee.extra_tax_percentage ? parseFloat(newEmployee.extra_tax_percentage) : null,
                care_days: newEmployee.care_days ? parseFloat(newEmployee.care_days) : null
            };

            if (newEmployee.id) {
                // Opdater eksisterende medarbejder
                await api.updateEmployee(newEmployee.id, employeeData);
            } else {
                // Opret ny medarbejder
                await api.createEmployee(employeeData);
            }
            setShowModal(false);
            // Genindlæs medarbejderlisten
            const response = await api.getEmployees();
            const filteredEmployees = response.employees.filter(employee => 
                employee.add_month === selectedMonth && 
                employee.add_year === selectedYear
            );
            setEmployees(filteredEmployees);
        } catch (error) {
            setModalError(error.message || 'Der opstod en fejl ved gem af medarbejder');
            console.error('Fejl ved gem af medarbejder:', error);
        } finally {
            setIsSaving(false);
        }
    };

    const handleEdit = async (employee) => {
        try {
            setModalError(null);
            const employeeData = await api.getEmployee(employee.id);
            
            // Formater datoer korrekt til lokal tid
            const formattedEmployeeData = {
                ...employeeData,
                employment_date: toLocalDateString(employeeData.employment_date),
                seniority_date: toLocalDateString(employeeData.seniority_date),
                pension_seniority_date: toLocalDateString(employeeData.pension_seniority_date),
                ff_seniority_date: toLocalDateString(employeeData.ff_seniority_date)
            };
            
            setNewEmployee(formattedEmployeeData);
            setShowModal(true);
        } catch (error) {
            setModalError('Der opstod en fejl ved hentning af medarbejderdata');
            console.error('Fejl ved hentning af medarbejderdata:', error);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Er du sikker på, at du vil slette denne medarbejder?')) {
            try {
                setError(null);
                await api.deleteEmployee(id);
                // Genindlæs medarbejderlisten
                const data = await api.getEmployees();
                const filteredData = data.filter(employee => 
                    employee.add_month === selectedMonth && 
                    employee.add_year === selectedYear
                );
                setEmployees(filteredData);
            } catch (error) {
                setError('Der opstod en fejl ved sletning af medarbejder');
                console.error('Fejl ved sletning af medarbejder:', error);
            }
        }
    };

    const handlePrevStep = () => {
        if (activeStep > 1) {
            setActiveStep(activeStep - 1);
            const modalContent = document.querySelector('.employee-modal-content');
            if (modalContent) {
                modalContent.scrollTop = 0;
            }
        }
    };

    // Hjælpefunktion til at finde felt-konfiguration
    // const getFieldConfig = (fieldName) => employeeFields.find(f => f.field_name === fieldName);

    const handleCopyFrom = async (type, id) => {
        try {
            const response = await api.copyFromTemplateOrEmployee(type, id);
            
            // Formater datoer korrekt
            const formattedData = {
                ...response,
                id: undefined,
                employment_date: toLocalDateString(response.employment_date),
                seniority_date: toLocalDateString(response.seniority_date),
                pension_seniority_date: toLocalDateString(response.pension_seniority_date),
                ff_seniority_date: toLocalDateString(response.ff_seniority_date)
            };
            
            // Opdater newEmployee med kopierede data
            setNewEmployee(prev => ({
                ...prev,
                ...formattedData,
                // Behold personlige oplysninger og datoer
                cpr: prev.cpr,
                employee_number: prev.employee_number,
                first_name: prev.first_name,
                last_name: prev.last_name,
                address: prev.address,
                postal_code: prev.postal_code,
                city: prev.city,
                country_code: prev.country_code,
                tin: prev.tin,
                extra_tax_percentage: prev.extra_tax_percentage,                
                email: prev.email,
                employment_date: prev.employment_date,
                seniority_date: prev.seniority_date,
                ff_seniority_date: prev.ff_seniority_date,
                pension_seniority_date: prev.pension_seniority_date,
                // Behold status og datoer
                status: 'pending',
                add_month: selectedMonth,
                add_year: selectedYear,
                // Gem reference til kopieringskilden
                copied_from_id: id,
                copied_from_type: type
            }));

            // Fjernet ubrugte state-opdateringer
        } catch (error) {
            console.error('Fejl ved kopiering af data:', error);
            setError('Der opstod en fejl ved kopiering af data');
        }
    };

    // Statisk mapping af sektioner og undersektioner
    const SECTION_CONFIG = {
        stamdata: {
            name: 'Stamdata',
            icon: 'fas fa-user',
            description: 'Grundlæggende personoplysninger og kontaktinformation'
        },
        bank: {
            name: 'Bank- og skatteoplysninger',
            icon: 'fas fa-money-check',
            description: 'Kontooplysninger og skattekort'
        },
        employment: {
            name: 'Ansættelsesdata',
            icon: 'fas fa-briefcase',
            description: 'Information om ansættelsesforhold og arbejdssted'
        },
        setup: {
            name: 'Lønopsætning',
            icon: 'fas fa-cog',
            description: 'Grundlæggende lønindstillinger'
        },
        salary: {
            name: 'Løn',
            icon: 'fas fa-money-bill',
            description: 'Løn og pensionsordninger'
        },
        benefits: {
            name: 'Personalegoder',
            icon: 'fas fa-gift',
            description: 'Fri telefon og andre personalegoder'
        },
        vacation: {
            name: 'Ferieordning',
            icon: 'fas fa-umbrella-beach',
            description: 'Ferie, feriefridage og andre ordninger'
        },
        notes: {
            name: 'Noter',
            icon: 'fas fa-sticky-note',
            description: 'Yderligere information og bemærkninger'
        }
    };

    // Mapping af backend-sektioner til hovedsteps
    const sectionGroups = [
        {
            label: 'Stamdata',
            icon: 'fas fa-user',
            sections: ['stamdata', 'bank']
        },
        {
            label: 'Ansættelse',
            icon: 'fas fa-briefcase',
            sections: ['employment', 'setup']
        },
        {
            label: 'Løn & Goder',
            icon: 'fas fa-money-bill',
            sections: ['salary', 'benefits']
        },
        {
            label: 'Ferie',
            icon: 'fas fa-umbrella-beach',
            sections: ['vacation', 'notes']
        }
    ];

    // Dynamisk step-navigation
    const renderStepNavigation = () => (
        <div className="employee-modal-steps">
            {sectionGroups.map((group, idx) => (
                <div
                    key={group.label}
                    className={`step ${activeStep === idx + 1 ? 'active' : ''} ${activeStep > idx + 1 ? 'completed' : ''}`}
                >
                    <div className="step-number">{idx + 1}</div>
                    <div className="step-label">
                        {group.icon && <i className={group.icon} style={{ marginRight: 6 }}></i>}
                        {group.label}
                    </div>
                </div>
            ))}
        </div>
    );

    // Opdater renderStepContent funktionen
    const renderStepContent = () => {
        const group = sectionGroups[activeStep - 1];
        // Saml alle felter fra de sektioner, der hører til dette hovedstep
        const fields = employeeFields.filter(f => group.sections.includes(f.section));
        // Gruppér felter pr. sektion
        const fieldsBySection = group.sections.reduce((acc, sectionId) => {
            acc[sectionId] = fields.filter(f => f.section === sectionId);
            return acc;
        }, {});

                return (
                    <div className="step-content">
                {/* Kopier fra-sektion kun på første step */}
                {activeStep === 1 && (
                        <div className="copy-from-section">
                            <div className="form-group">
                                <label>Kopier fra:</label>
                                <select 
                                value={newEmployee.copyFromType || ''}
                                onChange={e => {
                                        const type = e.target.value;
                                        setNewEmployee(prev => ({ ...prev, copyFromType: type }));
                                    }}
                                >
                                    <option value="">Vælg...</option>
                                    <option value="template">Skabelon</option>
                                    <option value="employee">Medarbejder</option>
                                </select>
                            </div>
                            {newEmployee.copyFromType === 'template' && (
                                <div className="form-group">
                                    <label>Vælg skabelon:</label>
                                <select onChange={e => handleCopyFrom('template', e.target.value)}>
                                        <option value="">Vælg skabelon...</option>
                                        {templates.map(template => (
                                        <option key={template.id} value={template.id}>{template.name}</option>
                                        ))}
                                    </select>
                                </div>
                            )}
                            {newEmployee.copyFromType === 'employee' && (
                                <div className="form-group">
                                    <label>Vælg medarbejder:</label>
                                <select onChange={e => handleCopyFrom('employee', e.target.value)}>
                                        <option value="">Vælg medarbejder...</option>
                                        {allEmployees.map(employee => (
                                        <option key={employee.id} value={employee.id}>{employee.first_name} {employee.last_name}</option>
                                        ))}
                                    </select>
                                </div>
                            )}
                                    </div>
                                )}
                {/* Undersektioner med felter */}
                {group.sections.map(sectionId => {
                    const sectionFields = fieldsBySection[sectionId];
                    if (!sectionFields || sectionFields.length === 0) return null;
                    const sectionInfo = SECTION_CONFIG[sectionId];
                    if (!sectionInfo) return null;

                    return (
                        <div key={sectionId} className="employee-section">
                            <div className="section-header">
                                {sectionInfo.icon && <i className={sectionInfo.icon}></i>}
                                <div>
                                    <h3>{sectionInfo.name}</h3>
                                    {sectionInfo.description && <div className="section-description">{sectionInfo.description}</div>}
                                </div>
                            </div>
                            <div className="section-content">
                                {/* NemKonto feltet altid først i bank-sektionen */}
                                {sectionId === 'bank' && (
                                    <div className="employee-form-group" style={{ gridColumn: '1 / -1', marginBottom: '1rem' }}>
                                        <label style={{ margin: 0 }}>Anvend NemKonto</label>
                                    <div className="checkbox-group">
                                        <input
                                            type="checkbox"
                                                checked={!!newEmployee.use_nemkonto}
                                                onChange={e => setNewEmployee({ ...newEmployee, use_nemkonto: e.target.checked })}
                                                style={{ margin: 0 }}
                                        />
                                    </div>
                                </div>
                                )}
                                {sectionFields
                                  .filter(f => f.is_active && (f.payroll_system === 'all' || f.payroll_system === payrollSystem.toLowerCase()))
                                  .map(field => {
                                    // Skip use_nemkonto da det er håndteret separat
                                    if (field.field_name === 'use_nemkonto') return null;
                                    
                                    // Skjul reg_number og account_number hvis NemKonto er valgt
                                    if (newEmployee.use_nemkonto && (field.field_name === 'reg_number' || field.field_name === 'account_number')) {
                                        return null;
                                    }

                                    // Vis kun TIN-feltet hvis country_code ikke er 'DK'
                                    if (field.field_name === 'tin' && (newEmployee.country_code || '').toLowerCase() === 'dk') {
                                        return null;
                                    }

                                    // Særlig rendering for arbejdsfunktionskode (job_function_code)
                                    if (field.field_name === 'job_function_code') {
                                        return (
                                            <div className="employee-form-group" key={field.field_name}>
                                                <label>{field.display_name}{field.is_required && ' *'}</label>
                                                <SearchableSelect
                                                    value={newEmployee[field.field_name] || ''}
                                                    options={discoCodes.disco_codes.map(code => ({
                                                        id: code.code,
                                                        name: `${code.code} - ${code.name}`
                                                    }))}
                                                    onChange={val => setNewEmployee({ ...newEmployee, [field.field_name]: val })}
                                                    placeholder="Vælg arbejdsfunktionskode..."
                                                    showCodeInParentheses={false}
                                                />
                                                {field.help_text && <div className="form-help-text">{field.help_text}</div>}
                    </div>
                );
                                    }

                                    // Særlig rendering for afregningsform (payment_form_id)
                                    if (field.field_name === 'salary_group') {
                return (
                                            <div className="employee-form-group" key={field.field_name}>
                                                <label>{field.display_name}{field.is_required && ' *'}</label>
                                  <select
                                                    value={newEmployee[field.field_name] || ''}
                                                    onChange={e => setNewEmployee({ ...newEmployee, [field.field_name]: e.target.value })}
                                                >
                                                    <option value="">Vælg afregningsform...</option>
                                    {afregningsformer.map(form => (
                                        <option key={form.id} value={form.id}>
                                            {form.name}
                                        </option>
                                    ))}
                                </select>
                                                {field.help_text && <div className="form-help-text">{field.help_text}</div>}
                            </div>
                                        );
                                    }

                                    // Særlig rendering for 'other' feltet under 'notes' sektionen
                                    if (field.field_name === 'other' && sectionId === 'notes') {
                                        return (
                                            <div className="employee-form-group span-full" key={field.field_name}>
                                                <label>{field.display_name}{field.is_required && ' *'}</label>
                                                <textarea
                                                    value={newEmployee[field.field_name] || ''}
                                                    onChange={e => setNewEmployee({ ...newEmployee, [field.field_name]: e.target.value })}
                                                    rows={6}
                                                    placeholder="Noter eventuelle særlige forhold, tillæg eller ordninger"
                                                />
                                                {field.help_text && <div className="form-help-text">{field.help_text}</div>}
                                            </div>
                                        );
                                    }

                                    // Særlig info for 'Fri bil'
                                    const isFreeCar = field.field_name === 'free_car';
                                    // Standard felt rendering
                                    switch (field.field_type) {
                                        case 'dropdown':
                return (
                                                <div className="employee-form-group" key={field.field_name}>
                                                    <label>{field.display_name}{field.is_required && ' *'}</label>
                                  <select
                                                        value={newEmployee[field.field_name] || ''}
                                                        onChange={e => setNewEmployee({ ...newEmployee, [field.field_name]: e.target.value })}
                                  >
                                                        <option value="">Vælg...</option>
                                                        {field.dropdown_list_id && dropdownItems[field.dropdown_list_id]?.map(item => (
                                      <option key={item.id} value={item.value}>{item.display_text}</option>
                                    ))}
                                  </select>
                                                    {field.help_text && <div className="form-help-text">{field.help_text}</div>}
                    </div>
                );
                                        case 'date':
                return (
                                                <div className="employee-form-group" key={field.field_name}>
                                                    <label>{field.display_name}{field.is_required && ' *'}</label>
                                <input
                                    type="date"
                                                        value={newEmployee[field.field_name] || ''}
                                                        onChange={e => setNewEmployee({ ...newEmployee, [field.field_name]: e.target.value })}
                                />
                                                    {field.help_text && <div className="form-help-text">{field.help_text}</div>}
                            </div>
                );
                                        case 'boolean':
                return (
                    <div className="employee-form-group" key={field.field_name}>
                        <label>{field.display_name}{field.is_required && ' *'}</label>
                                <div className="checkbox-group">
                                    <input
                                        type="checkbox"
                                checked={!!newEmployee[field.field_name]}
                                onChange={e => setNewEmployee({ ...newEmployee, [field.field_name]: e.target.checked })}
                                style={{ margin: 0 }}
                                    />
                                </div>
                        {field.help_text && <div className="form-help-text">{field.help_text}</div>}
                        {isFreeCar && newEmployee[field.field_name] && (
                            <div className="form-help-text" style={{ color: '#d97706', fontWeight: 500 }}>
                                Husk at uploade kopi af leasingkontrakt eller dokumentation for beskatningsværdi under Fildeling → Input.
                            </div>
                            )}
                    </div>
                );
                                        case 'number':
                return (
                    <div className="employee-form-group" key={field.field_name}>
                        <label>{field.display_name}{field.is_required && ' *'}</label>
                                    <input
                                        type="text"
                            value={
                                inputRawValues[field.field_name] !== undefined
                                    ? inputRawValues[field.field_name]
                                    : (typeof newEmployee[field.field_name] === 'number' && !isNaN(newEmployee[field.field_name])
                                        ? formatAmount(newEmployee[field.field_name])
                                        : '')
                            }
                                        onChange={e => {
                                setInputRawValues({ ...inputRawValues, [field.field_name]: e.target.value });
                                        }}
                                        onBlur={e => {
                                const val = parseAmount(e.target.value);
                                setNewEmployee({ ...newEmployee, [field.field_name]: val });
                                setInputRawValues(prev => ({ ...prev, [field.field_name]: undefined }));
                            }}
                            inputMode="decimal"
                        />
                        {field.help_text && <div className="form-help-text">{field.help_text}</div>}
                                    </div>
                );
                                        case 'text':
                return (
                    <div className="employee-form-group" key={field.field_name}>
                        <label>{field.display_name}{field.is_required && ' *'}</label>
                        <input
                            type="text"
                            value={newEmployee[field.field_name] || ''}
                            onChange={e => {
                                let value = e.target.value;
                                // Særlig håndtering for CPR-nummer
                                if (field.field_name === 'cpr') {
                                    // Fjern alle ikke-cifre og bindestreg
                                    value = value.replace(/[^\d-]/g, '');
                                    // Tilføj bindestreg efter 6 cifre hvis den ikke allerede er der
                                    if (value.length > 6 && !value.includes('-')) {
                                        value = value.slice(0, 6) + '-' + value.slice(6);
                                    }
                                    // Begræns til 11 tegn (DDMMYY-XXXX)
                                    value = value.slice(0, 11);
                                }
                                setNewEmployee({ ...newEmployee, [field.field_name]: value });
                            }}
                            placeholder={field.field_name === 'cpr' ? 'DDMMYY-XXXX' : ''}
                        />
                        {field.help_text && <div className="form-help-text">{field.help_text}</div>}
                    </div>
                );
                                        default:
                return (
                                                <div className="employee-form-group" key={field.field_name}>
                                                    <label>{field.display_name}{field.is_required && ' *'}</label>
                                    <input
                                        type="text"
                                                        value={newEmployee[field.field_name] || ''}
                                                        onChange={e => setNewEmployee({ ...newEmployee, [field.field_name]: e.target.value })}
                                                    />
                                                    {field.help_text && <div className="form-help-text">{field.help_text}</div>}
                                    </div>
                                            );
                                    }
                                })}
                                </div>
                            </div>
                    );
                })}
                    </div>
                );
    };

    useEffect(() => {
        const fetchTemplates = async () => {
            try {
                const data = await api.getEmployeeTemplates();
                setTemplates(data);
            } catch (err) {
                // evt. fejlbesked
        }
    };
        fetchTemplates();
    }, []);

    return (
        <div className="employee-management">
            {error && (
                <div className="error-message">
                    {error}
                </div>
            )}
            
            <div className="emp-table-header">
                <div className="emp-right-buttons">
                    <button 
                        className="emp-add-new-button"
                        onClick={handleAddNew}
                    >
                        <i className="fas fa-plus"></i> Tilføj ny medarbejder
                    </button>
                </div>
            </div>

            {loading ? (
                <div className="loading">Indlæser medarbejdere...</div>
            ) : employees.length === 0 ? (
                <div className="no-data">Ingen medarbejdere fundet for den valgte periode</div>
            ) : (
                <table className="employee-table">
                    <thead>
                        <tr>
                            <th>Medarbejdernr.</th>
                            <th>Navn</th>
                            <th>Email</th>
                            <th>Startdato</th>
                            <th>Ansættelsestype</th>
                            <th>Afdeling</th>
                            <th>Måned/År</th>
                            <th>Status</th>
                            <th>Handlinger</th>
                        </tr>
                    </thead>
                    <tbody>
                        {employees.map(employee => (
                            <tr key={employee.id}>
                                <td>{employee.employee_number}</td>
                                <td>{`${employee.first_name} ${employee.last_name}`}</td>
                                <td>{employee.email}</td>
                                <td>{formatDate(employee.employment_date)}</td>
                                <td>{employee.employment_type}</td>
                                <td>{employee.department}</td>
                                <td>{`${employee.add_month}/${employee.add_year}`}</td>
                                <td className={`status-${employee.status}`}>
                                    {employee.status === 'pending' ? 'Afventer' : 
                                     employee.status === 'processed' ? 'Behandlet' : 
                                     employee.status === 'modified' ? 'Ændret' : employee.status}
                                </td>
                                <td className="emp-action-buttons">
                                    <button 
                                        className="emp-action-btn emp-edit-btn"
                                        onClick={() => handleEdit(employee)}
                                        title="Rediger"
                                    >
                                        <i className="fas fa-edit"></i>
                                    </button>
                                    <button 
                                        className="emp-action-btn emp-delete-btn"
                                        onClick={() => handleDelete(employee.id)}
                                        title="Slet"
                                    >
                                        <i className="fas fa-trash"></i>
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}

            {showModal && (
                <div className="employee-modal-backdrop">
                    <div className="employee-modal">
                        <button 
                            className="close-button"
                            onClick={() => setShowModal(false)}
                            title="Luk"
                        >
                            ×
                        </button>
                        <h2>{newEmployee.id ? 'Rediger medarbejder' : 'Tilføj ny medarbejder'}</h2>
                        
                        {modalError && (
                            <div className="error-message">
                                {modalError}
                            </div>
                        )}

                        {renderStepNavigation()}

                        <div className="employee-modal-content">
                            {renderStepContent()}
                        </div>

                        <div className="employee-modal-footer">
                            <div className="modal-navigation">
                                {activeStep > 1 && (
                                    <button className="nav-btn prev-btn" onClick={handlePrevStep}>
                                        <i className="fas fa-chevron-left"></i> Tilbage
                                    </button>
                                )}
                            </div>
                            <div className="modal-actions">
                                <button className="employee-cancel-btn" onClick={() => setShowModal(false)}>Annuller</button>
                                {activeStep < 4 ? (
                                    <button className="employee-save-btn" onClick={handleNextStep}>
                                        Næste <i className="fas fa-chevron-right"></i>
                                    </button>
                                ) : (
                                    <button 
                                        className="employee-save-btn" 
                                        onClick={handleSave}
                                        disabled={isSaving}
                                    >
                                        {isSaving ? (
                                            <>
                                                <i className="fas fa-spinner fa-spin"></i> Gemmer...
                                            </>
                                        ) : (
                                            'Gem medarbejder'
                                        )}
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EmployeeManagement; 